import { ChatSession, Message, ToolApproval, MessageType, ExecutionLedger } from "../../types";
import { isAuthMockMode } from "../auth";
import { supabase } from "../../lib/supabaseClient";

import { MCP_BASE_URL as BACKEND_URL } from "../../config";

export const sendMessage = async (
    sessionId: string | null,
    text: string,
    file?: File,
    signal?: AbortSignal,
    onThinkingUpdate?: (thinkingText: string) => void,
    onResponseUpdate?: (responseText: string) => void,
    onLedgerUpdate?: (ledger: ExecutionLedger) => void,
    existingLedger?: ExecutionLedger | null,
    onToolEvent?: (event: any) => void,
    interventionId?: string,
    interventionAnswer?: any,
): Promise<{ session: ChatSession; userMsg: Message; aiMsg: Message; }> => {
    
    if (isAuthMockMode) {
        const userMsg: Message = { id: Date.now().toString(), type: "user", content: text, timestamp: new Date() };
        const aiMsg: Message = { id: (Date.now() + 1).toString(), type: "ai", content: "AI is in mock mode.", timestamp: new Date() };
        return { session: { id: "mock", title: "Mock", messages: [userMsg, aiMsg], createdAt: new Date() }, userMsg, aiMsg };
    }

    const { data: sessionData } = await supabase!.auth.getSession();
    const token = sessionData?.session?.access_token;

    // Create session if not provided
    let finalSessionId = sessionId;
    let sessionTitle = text.slice(0, 30);
    
    if (!finalSessionId) {
        const { data: userResult } = await supabase!.auth.getUser();
        const userId = userResult.user?.id;
        const { data: newSession, error } = await supabase!
            .from("chat_sessions")
            .insert({ user_id: userId, title: sessionTitle })
            .select()
            .single();
        if (error) throw new Error(error.message);
        finalSessionId = newSession.id;
    }

    try {
        if (onThinkingUpdate) onThinkingUpdate("Connecting to Neura...");

        const response = await fetch(`${BACKEND_URL}/api/chat/stream`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
                session_id: finalSessionId,
                text: text,
                intervention_id: interventionId,
                intervention_answer: interventionAnswer
            }),
            signal
        });

        if (!response.ok) {
            const errData = await response.json().catch(() => ({}));
            throw new Error(errData.error || `Backend Error: ${response.status}`);
        }

        // Read the SSE stream
        const reader = response.body!.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let fullContent = '';
        let thinkingCleared = false;
        let lastAiMsg: Message = { id: "stream-ai", type: "ai", content: "", timestamp: new Date() };

        while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop() || '';

            for (const line of lines) {
                const trimmed = line.trim();
                if (!trimmed.startsWith('data:')) continue;
                const dataStr = trimmed.slice(5).trim();
                if (!dataStr || dataStr === '{"ping": "pong"}') continue;

                try {
                    const event = JSON.parse(dataStr);

                    switch (event.type) {
                        case 'thinking':
                            if (onThinkingUpdate) onThinkingUpdate(event.content);
                            break;

                        case 'token':
                            if (!thinkingCleared) {
                                thinkingCleared = true;
                                if (onThinkingUpdate) onThinkingUpdate('__DONE__');
                            }
                            fullContent += event.content;
                            if (onResponseUpdate) onResponseUpdate(fullContent);
                            break;

                        case 'ledger':
                            if (onLedgerUpdate) onLedgerUpdate(event.ledger);
                            if (onToolEvent) onToolEvent(event);
                            // Reset for next AI turn
                            fullContent = '';
                            thinkingCleared = false;
                            if (onResponseUpdate) onResponseUpdate('');
                            break;

                        case 'ledger_update':
                            if (onLedgerUpdate) onLedgerUpdate(event.ledger);
                            break;

                        case 'tool_start':
                            if (onToolEvent) onToolEvent(event);
                            // Reset for next AI turn
                            fullContent = '';
                            thinkingCleared = false;
                            if (onResponseUpdate) onResponseUpdate('');
                            if (onThinkingUpdate) onThinkingUpdate(`Executing ${event.tool}...`);
                            break;

                        case 'tool_result':
                            if (onToolEvent) onToolEvent(event);
                            fullContent = '';
                            thinkingCleared = false;
                            if (onResponseUpdate) onResponseUpdate('');
                            break;

                        case 'tool_pending':
                            lastAiMsg = {
                                id: event.message_id || `tool-${Date.now()}`,
                                type: 'ai',
                                content: event.content || '',
                                timestamp: new Date(),
                                toolApproval: event.approval
                            };
                            if (onToolEvent) onToolEvent(event);
                            break;

                        case 'intervention_required':
                            lastAiMsg = {
                                id: event.payload.intervention_id || `int-${Date.now()}`,
                                type: 'ai',
                                content: '',
                                timestamp: new Date(),
                                intervention: event.payload
                            };
                            if (onToolEvent) onToolEvent(event);
                            break;

                        case 'tool_error':
                            if (onToolEvent) onToolEvent(event);
                            break;

                        case 'error':
                            console.error('[Stream] Error from backend:', event.content);
                            break;

                        case 'done':
                            if (onThinkingUpdate) onThinkingUpdate('__DONE__');
                            if (onResponseUpdate) onResponseUpdate('');
                            break;
                    }
                } catch {
                    // Skip malformed events
                }
            }
        }

        // The stream is done; return the session info.
        // The DashboardPage will do a final refresh from Supabase to get clean data.
        if (fullContent && !lastAiMsg.content) {
            lastAiMsg = {
                id: `temp-ai-${Date.now()}`,
                type: 'ai',
                content: fullContent,
                timestamp: new Date()
            };
        }
        
        return {
            session: { id: finalSessionId as string, title: sessionTitle, messages: [], createdAt: new Date() },
            userMsg: { id: "temp-user", type: "user", content: text, timestamp: new Date() },
            aiMsg: lastAiMsg,
        };

    } catch (err: any) {
        if (onThinkingUpdate) onThinkingUpdate("__DONE__");
        if (err.name === "AbortError") throw err;
        throw err;
    }
};

export const executeAction = async (
    sessionId: string,
    approval: ToolApproval,
    action: "approve" | "reject",
    onStepUpdate?: (stepIndex: number, status: "pending" | "in-progress" | "completed" | "failed") => void,
    updateMessageId?: string,
    signal?: AbortSignal,
    updatedArgs?: any
): Promise<{ id: string; type: MessageType; content: string; timestamp: Date; toolApproval?: ToolApproval }> => {
    
    if (isAuthMockMode) {
        return { id: "mock", type: "system", content: "Mock mode", timestamp: new Date() };
    }

    const { data: sessionData } = await supabase!.auth.getSession();
    const token = sessionData?.session?.access_token;

    try {
        const response = await fetch(`${BACKEND_URL}/api/chat/approve`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
                session_id: sessionId,
                message_id: updateMessageId,
                action: action,
                args: updatedArgs // In case they modified args
            }),
            signal
        });

        if (!response.ok) {
            const errData = await response.json().catch(() => ({}));
            throw new Error(errData.error || `Backend Error: ${response.status}`);
        }

        // Return a dummy placeholder. Realtime will update the UI.
        return { 
            id: "temp-sys", 
            type: "system", 
            content: action === "approve" ? "Execution started..." : "Action cancelled.", 
            timestamp: new Date() 
        };
    } catch (err: any) {
        if (err.name === "AbortError") throw err;
        throw err;
    }
};

export const modifyCardParams = async (toolName: string, currentArgs: any, instruction: string): Promise<any> => {
    // This could also be moved to the backend, but for now we just use OpenRouter directly
    // since it's just a simple utility function.
    const apiKey = (import.meta as any).env.VITE_OPENROUTER_API_KEY || (window as any).OPENROUTER_API_KEY || "";
    
    const response = await fetch("https://openrouter.ai/api/v1/chat/completions", {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${apiKey}`,
            "HTTP-Referer": window.location.origin,
            "X-Title": "Neura AI",
        },
        body: JSON.stringify({
            messages: [{ role: "system", content: `Modify parameters for ${toolName} based on: ${instruction}. Current: ${JSON.stringify(currentArgs)}. Return JSON only.` }],
            model: "openai/gpt-oss-120b:free",
            temperature: 0.1,
            response_format: { type: "json_object" }
        })
    });

    if (!response.ok) throw new Error("Failed to modify parameters");
    const data = await response.json();
    return JSON.parse(data.choices?.[0]?.message?.content || "{}");
};
